import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import TrustBar from './components/TrustBar';
import Projects from './components/Projects';
import Repositories from './components/Repositories';
import Apps from './components/Apps';
import Career from './components/Career';
import Skills from './components/Skills';
import Certifications from './components/Certifications';
import Publications from './components/Publications';
import Research from './components/Research';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ErrorState from './components/ErrorState';
import ScrollToTop from './components/ScrollToTop';
import Reveal from './components/Reveal';
import SectionSkeleton from './components/SectionSkeleton';
import useGitHub from './hooks/useGitHub';
import { CONFIG } from './utils/constants';

// New key so visitors who had a device-derived value stored under the old
// 'theme' key start from the light default like everyone else.
var THEME_KEY = 'theme-preference';

const App = () => {
  // Light is the default for every first visit; the OS preference is
  // deliberately ignored. The toggle persists the visitor's explicit choice.
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) === 'dark'; } catch (e) { return false; }
  });

  const { profile, repos, loading, error, refetch } = useGitHub(CONFIG.githubUsername);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }
    localStorage.setItem(THEME_KEY, darkMode ? 'dark' : 'light');
  }, [darkMode]);

  var toggleDarkMode = () => setDarkMode(!darkMode);

  // Only the GitHub-backed section depends on the network. Everything else is
  // static and renders immediately, so a slow or rate-limited API never blanks
  // the whole portfolio behind a spinner.
  var repositoriesContent;
  if (loading) {
    repositoriesContent = <SectionSkeleton label="Loading repositories" />;
  } else if (error) {
    repositoriesContent = (
      <section id="repositories" className="py-24">
        <div className="max-w-7xl 2xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8">
          <ErrorState message={'Could not load GitHub repositories. ' + error} onRetry={refetch} />
        </div>
      </section>
    );
  } else {
    repositoriesContent = <Repositories repos={repos} />;
  }

  return (
    <div className="min-h-screen bg-surface-950 transition-colors duration-300">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[70] focus:px-4 focus:py-2.5 focus:rounded-lg focus:bg-primary-600 focus:text-white focus:font-semibold focus:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
      >
        Skip to content
      </a>
      <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <main id="main">
        <Hero profile={error ? null : profile} />
        <TrustBar />
        <Reveal as="div"><Projects /></Reveal>
        <Reveal as="div"><Apps repos={error ? [] : repos} /></Reveal>
        <Reveal as="div"><Career /></Reveal>
        <Reveal as="div" aria-busy={loading || undefined}>{repositoriesContent}</Reveal>
        <Reveal as="div"><Skills /></Reveal>
        <Reveal as="div"><Certifications /></Reveal>
        <Reveal as="div"><Publications /></Reveal>
        <Reveal as="div"><Research /></Reveal>
        <Reveal as="div"><Contact /></Reveal>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default App;
